import { useCallback, useEffect, useRef, useState } from 'react'
import {
  LAST_TAB_KEY,
  parsePath,
  readInitialRoute,
  routeForTab,
  toPath,
  type Route,
  type Tab,
} from './route'

type Stamp = { reachout: 1; i: number; back?: string }

function isStamp(state: unknown): state is Stamp {
  return !!state && typeof state === 'object' && (state as Stamp).reachout === 1
}

/**
 * Keep the visible screen in the address bar and on the history stack.
 *
 * `goToTab` restores that tab's last nested screen (campaign editor, open
 * filter, …) so switching away and back does not throw away where you were.
 *
 * `goBack(fallback)` only pops real history when the current entry was
 * itself pushed with that same fallback as its `back` target (via
 * `navigate(next, { back: fallback })`) — not merely because something has
 * been pushed this session. Anything else (a deep link, a tab switch that
 * restored this screen, a `back` that does not match) replaces to the
 * fallback instead, so "close" always lands on the intended parent rather
 * than whatever the browser's previous entry happens to be.
 */
export function useRoute() {
  const [route, setRoute] = useState<Route>(() => {
    const initial = readInitialRoute()
    window.history.replaceState({ reachout: 1, i: 0 } satisfies Stamp, '', toPath(initial))
    return initial
  })
  const indexRef = useRef(0)
  const lastByTab = useRef<Partial<Record<Tab, Route>>>({ [route.tab]: route })

  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      const next = parsePath(window.location.pathname)
      indexRef.current = isStamp(event.state) ? event.state.i : 0
      lastByTab.current[next.tab] = next
      setRoute(next)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(LAST_TAB_KEY, route.tab)
    } catch {
      // Remembering the tab is a convenience, not a feature.
    }
  }, [route.tab])

  const navigate = useCallback((next: Route, opts?: { replace?: boolean; back?: Route }) => {
    const path = toPath(next)
    const back = opts?.back ? toPath(opts.back) : undefined
    lastByTab.current[next.tab] = next
    if (opts?.replace) {
      window.history.replaceState(
        { reachout: 1, i: indexRef.current, back } satisfies Stamp,
        '',
        path,
      )
      setRoute(next)
      return
    }
    if (path === window.location.pathname) return
    indexRef.current += 1
    window.history.pushState({ reachout: 1, i: indexRef.current, back } satisfies Stamp, '', path)
    setRoute(next)
  }, [])

  const goToTab = useCallback(
    (tab: Tab) => {
      if (tab === route.tab) return
      navigate(lastByTab.current[tab] ?? routeForTab(tab))
    },
    [navigate, route.tab],
  )

  const goBack = useCallback(
    (fallback: Route) => {
      const state: unknown = window.history.state
      if (isStamp(state) && state.back === toPath(fallback)) {
        window.history.back()
        return
      }
      navigate(fallback, { replace: true })
    },
    [navigate],
  )

  return { route, navigate, goToTab, goBack }
}
